import { useToast } from 'vue-toastification'
import { RESPONSE_ERRORS } from '@/constants/RESPONSE_ERRORS'
import { formatAiServiceError } from '@/utils/vai/formatAiServiceError'

const TIMEOUT_ERROR_CODES = ['ECONNABORTED', 'ETIMEDOUT']

type VaiRequestError = {
  code?: string
  config?: {
    ignoreGlobalNotify?: boolean
  }
  response?: {
    status?: number
    data?: unknown
  }
}

type RelatedFeaturesErrorData = {
  errors?: {
    relatedFeatures?: unknown[]
  }
}

const getFallbackMessage = (status: number): string => {
  return (RESPONSE_ERRORS as Record<number, string>)[status] || RESPONSE_ERRORS.default
}

const getErrorMessage = (error: VaiRequestError): string => {
  const status = error.response?.status

  if (!status) {
    return RESPONSE_ERRORS.network
  }

  // formatAiServiceError already keeps a 5xx body out of the toast.
  return formatAiServiceError(error, getFallbackMessage(status))
}

// The vaiClient counterpart of apiErrorHandler: one toast per failed vAI
// request, worded by formatAiServiceError (ai-parser's `message`, or `detail`
// on a pydantic 422). A 404 is a toast too, never the NotFound page.
export const vaiErrorHandler = (error: VaiRequestError): void => {
  // A timeout or a transport abort is not reported, the same as in httpClient.
  if (error?.config?.ignoreGlobalNotify || TIMEOUT_ERROR_CODES.includes(String(error?.code))) {
    return
  }

  // Deleting a prompt still used by automations: the caller opens the related features modal.
  if ((error?.response?.data as RelatedFeaturesErrorData | undefined)?.errors?.relatedFeatures?.length) {
    return
  }

  const toast = useToast()
  const errorMessage = getErrorMessage(error)

  // Same as apiErrorHandler: while offline every pending request fails at once, so the
  // earlier toasts are dropped and a single network message stays.
  if (errorMessage === RESPONSE_ERRORS.network) {
    toast.clear()
  }

  toast.error(errorMessage)
}
